import { logOnToZephr, requestMagicLink, requestForgotPassword } from "./utils";
import { emailSchema, passwordSchema, loginSchema } from "./schema";
import { throttle } from "throttle-debounce";
import { useMinheight } from "../../../../aw-convertr/implementations/webinars/frontend/src/utils";

const { useEffect, useState, useRef } = window.React;

export const LoginForm = ({
    rounded = true,
    isLoading,
    setIsLoading,

    LoginTitleComponent = null,
} = {}) => {

    const [credErrMessage, setCredErrMessage] = useState( null );
    const [passwordResetMessage, setPasswordResetMessage] = useState( null );
    const [magicLinkMessage, setMagicLinkMessage] = useState( null );
    const [usePassword, setUsePassword] = useState( false );

    const refEmail = useRef();
    const refPassword = useRef();
    const refForm = useRef();
    const refFormFieldWrapper = useRef();

    const [canLogin, setCanLogin] = useState( false );
    const [canResetPasswordOrMagicLink, setCanResetPasswordOrMagicLink] = useState( false );

    const [isPasswordResetMode, setIsPasswordResetMode] = useState( false );

    const [isSendingMagicLink, setIsSendingMagicLink] = useState( false );

    const unsetMinHeightAndWidth = (refForm) => {
        if( refForm.current ){
            refForm.current.parentElement.style.minHeight = `initial`;
            refForm.current.parentElement.style.minWidth = `initial`;
        }
    }

    window.addEventListener('resize', throttle( 200, () => {
        unsetMinHeightAndWidth(refForm);
    } ) );

    const startPasswordResetProcess = (e) => {
        e?.preventDefault();
        try{
            // Setting minimum height so that form maintains same height during UI changes in form content.
            const formMinHeight = refForm.current.parentElement.getBoundingClientRect().height;
            const formMinWidth = refForm.current.parentElement.getBoundingClientRect().width;
            refForm.current.parentElement.style.minHeight = `${formMinHeight}px`;
            refForm.current.parentElement.style.minWidth = `${formMinWidth}px`;
        }catch(err){
            console.log( 'Error setting height', err );
        }

        setIsPasswordResetMode( true );
    }

    const handleFormChange = async (e) => {

        const elPasswordInput = refForm.current?.querySelector( 'input[name="password"]' );
        const elEmailInput = refForm.current?.querySelector( 'input[name="email"]' );
        try{
            await loginSchema.validate( {email: elEmailInput.value, password: elPasswordInput.value} )
            setCanLogin( true );
        } catch( err ){
            setCanLogin( false );
        }

        try{
            const emailIsValid = await emailSchema.isValid( elEmailInput.value )
            setCanResetPasswordOrMagicLink( emailIsValid );
        } catch( err ){
            setCanResetPasswordOrMagicLink( false );
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        setMinHeightSubmitBtn();
        setMagicLinkMessage( null );
        setCredErrMessage( null );
        
        const elPasswordInput = e.target.querySelector( 'input[name="password"]' );
        const elEmailInput = e.target.querySelector( 'input[name="email"]' );
        try{
            await loginSchema.validate( {email: elEmailInput.value, password: elPasswordInput.value} )
            setIsLoading( true );

            try{
                const loginRes = await logOnToZephr( {emailVal: elEmailInput.value, passwordVal: elPasswordInput.value} );
                
                handleRes( {res: loginRes} );
                
            } catch( err ){
                console.warn( 'First Login attempt failed', err );
                
                try{
                    const loginResRetry = await logOnToZephr( {emailVal: elEmailInput.value, passwordVal: elPasswordInput.value, retry: true} );
                    
                    handleRes( {res: loginResRetry, retry: true} );
                } catch( errRetry ){
                    console.warn( 'Second Login attempt for legacy password failed', errRetry );
                    handleRes( {res: errRetry} );
                }
                
            }
        } catch( err ){
            setCredErrMessage( 'Check input fields' );
        }

    }

    const handleRes = ( {res, retry = false, cb} ) => {
        console.log( 'Login response: ', res );
        if( res && !res.status ){
            console.warn( 'No status in response.', res );
        }

        if( res && ( res.tracking_id ) ){
            handleSuccess( {res, cb} );
        } else {
            handleErrorMessages( res );
        }
    }

    const handleSuccess = ( {cb} ) => {
        if( typeof cb === 'function' ){
            setIsLoading( false );
            cb();
        } else {
            window.location.reload();
        }
    }

    const handleErrorMessages = ( err ) => {

        if( err && err.errorMessage ){
            console.log( 'err.errorMessage', err.errorMessage );
            setCredErrMessage( err.errorMessage );
        } else {
            setCredErrMessage( 'Something went wrong. Try again later.' );
        }
        setIsLoading( false );
        
    }

    const schemas = {
        email: emailSchema,
        password: passwordSchema,
    }

    const handleInputFieldValidation = async (e) => {

        const elInlineError = e.target.parentElement.querySelector( `[data-field-name="${e.target.name}"]` );
        if( !elInlineError ) return;
        try{
            const validation = await schemas[e.target.name].validate( e.target.value );

            if( elInlineError ){
                elInlineError.innerHTML = '';
            }
        } catch (err){
            
            if( elInlineError ){
                elInlineError.innerHTML = err?.errors?.[0] || '';
            }
        }

    }

    const refSubmit = React.useRef();

    const setMinHeightSubmitBtn = () => {
        const submitBtnHeight = refSubmit?.current?.getBoundingClientRect?.().height;

        if( submitBtnHeight && submitBtnHeight > 0 ){
            refSubmit.current.style.minHeight = `${submitBtnHeight}px`;
        }
    }

    const handleMagicLinkRequest = async ( ) => {
        setIsSendingMagicLink( true );
        setMagicLinkMessage( null );
        try{
            const res = await requestMagicLink( refEmail.current.value );
            const resJson = await res.json();
            setIsSendingMagicLink( false );
            setMagicLinkMessage( resJson?.message ? resJson.message : 'Could not request Magic Link. Try again later.' )
        } catch( err ){
            setMagicLinkMessage( 'Could not request Magic Link. Try again later.' );
            setIsSendingMagicLink( false );
            console.warn( 'Error requesting magic link', err );
        }
    }

    const handlePasswordReset = async () => {

        setIsLoading( true );
        setPasswordResetMessage( null );

        try{

            await schemas['email'].validate( refEmail.current.value );
            try{
                const res = await requestForgotPassword( refEmail.current.value );
                const resJson = await res.json();

                if( resJson?.message ){
                    setPasswordResetMessage( resJson.message );
                } else {
                    setPasswordResetMessage( 'Could not send password reset email. Try again later.' );
                }

                setIsLoading( false );
            } catch( err ){
                setPasswordResetMessage( 'Could not send password reset email. Try again later.' );
                console.warn( 'Error request password reset email', err );
                setIsLoading( false );
            }
        } catch( err ){
            setCredErrMessage( 'Enter valid email' );
        }
        
    }

    return (
        <>
        {
            isPasswordResetMode
                ?
                <Title text={'Reset Password'} />
                :
                <>
                {
                    LoginTitleComponent ?
                    <LoginTitleComponent /> : <Title text={"Log In"} />
                }
                </>
        }
        
        <form
            className={` m-0 pt-2`}
            onSubmit={handleSubmit}
            onChange={handleFormChange}
            ref={refForm}
        >
            <div
                ref={refFormFieldWrapper}
                className="d-flex flex-column gap-4 mb-0"
            >
                
                <div class="d-flex flex-column gap-2" >
                    <label
                        htmlFor="email-input"
                        className={`block text-sm font-medium mb-0`}
                    >
                        Email
                    </label>
                    <input
                        ref={refEmail}
                        disabled={isLoading}
                        type="email"
                        id="email-input"
                        name="email"
                        className={`py-2 px-3 block w-full border border-gray-200 ${rounded ? 'rounded-lg' : 'rounded-0'} text-sm
                            
                            disabled:opacity-50 disabled:pointer-events-none`}
                        placeholder="you@adweek.com"
                        onBlur={handleInputFieldValidation}
                    ></input>
                    <InlineInputFieldError target={'email'}/>
                </div>
                { !isPasswordResetMode &&
                
                <>
                { usePassword && 
                <div class="d-flex flex-column gap-2" >
                    <>
                        <label
                            for="password-input"
                            className={`block text-sm font-medium mb-0`}
                            
                        >
                            Password
                        </label>
                        <input
                            ref={refPassword}
                            disabled={isLoading}
                            type="password"
                            id="password-input"
                            name="password"
                            className={`py-2 px-3 block w-full border border-gray-200 ${rounded ? 'rounded-lg' : 'rounded-0'} text-sm
                                focus:border-blue-500 focus:ring-blue-500
                                disabled:opacity-50 disabled:pointer-events-none`}
                            placeholder=""
                            onBlur={handleInputFieldValidation}
                        ></input>
                        <InlineInputFieldError target={'password'}/>
                    </>
                </div>
                }

                { credErrMessage &&
                    <ErrorMessage credErrMessage={credErrMessage} setCredErrMessage={setCredErrMessage} />
                }
            
                { !usePassword &&
                <button 
                    className={`
                        btn position-relative border border-aw-webinar bg-aw-webinar text-white  ${rounded ? 'rounded-lg' : 'rounded-0'}
                    `}
                    onClick={() => setUsePassword( true )}
                >
                    Use Password
                </button>
                }
                { usePassword &&
                    <SignInButton isLoading={isLoading} canLogin={canLogin} refSubmit={refSubmit} rounded={rounded} />
                }


                { magicLinkMessage &&
                    <InlineMessage message={magicLinkMessage} />
                }

                <MagicLinkButton
                    isSendingMagicLink={isSendingMagicLink}
                    rounded={rounded}
                    handleMagicLinkRequest={handleMagicLinkRequest}
                    refEmail={refEmail}
                    disabled={ !canResetPasswordOrMagicLink }
                    cb={() => {
                        setCredErrMessage( null );
                    }}
                />
                <ForgotPasswordLink startPasswordResetProcess={startPasswordResetProcess} cb={() => {
                    setMagicLinkMessage(null);
                    setCredErrMessage( null );
                }} />
                </>
                }

                { isPasswordResetMode &&
                <>
                {passwordResetMessage &&
                    <InlineMessage message={passwordResetMessage} />
                }

                <ResetButton isLoading={isLoading} handlePasswordReset={handlePasswordReset} disabled={!canResetPasswordOrMagicLink} />
                <button 
                    className={`
                        bg-none border hover:bg-transparent hover:border-black hover:text-black hover:bg-slate-100
                        focus:text-black focus:bg-slate-100
                        ${rounded ? 'rounded-lg' : 'rounded-0'}
                    `}
                    onClick={() => {
                        setIsPasswordResetMode( false );
                        setPasswordResetMessage( null );
                    }}
                >
                    Cancel
                </button>
                </>
                }
            </div>
        </form>
        </>
    );
};


const MagicLinkButton = ({ isSendingMagicLink, rounded, handleMagicLinkRequest, disabled = false, cb = () => {} }) => {

    const ref = useRef();
    useMinheight( ref );
    return(
        <button
            disabled={isSendingMagicLink || disabled}
            className={`
                btn border border-aw-primary hover:border-black hover:text-black hover:bg-slate-100
                focus:text-black focus:bg-slate-100
                ${rounded ? 'rounded-lg' : 'rounded-0'}
                d-flex justify-content-center align-items-center
            `}
            onClick={() => {
                handleMagicLinkRequest( );
                cb();
            }}
            ref={ref}
            style={{minHeight: "50px"}}
        >
            { isSendingMagicLink ?
                <LoadingDots className={'text-aw-webinar'} />
                :
                <>
                <span className="d-inline-block mx-1">
                    Send Magic Link
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-magic d-inline-block mx-1" viewBox="0 0 16 16">
                    <path d="M9.5 2.672a.5.5 0 1 0 1 0V.843a.5.5 0 0 0-1 0zm4.5.035A.5.5 0 0 0 13.293 2L12 3.293a.5.5 0 1 0 .707.707zM7.293 4A.5.5 0 1 0 8 3.293L6.707 2A.5.5 0 0 0 6 2.707zm-.621 2.5a.5.5 0 1 0 0-1H4.843a.5.5 0 1 0 0 1zm8.485 0a.5.5 0 1 0 0-1h-1.829a.5.5 0 0 0 0 1zM13.293 10A.5.5 0 1 0 14 9.293L12.707 8a.5.5 0 1 0-.707.707zM9.5 11.157a.5.5 0 0 0 1 0V9.328a.5.5 0 0 0-1 0zm1.854-5.097a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L8.646 5.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0l1.293-1.293Zm-3 3a.5.5 0 0 0 0-.706l-.708-.708a.5.5 0 0 0-.707 0L.646 13.94a.5.5 0 0 0 0 .707l.708.708a.5.5 0 0 0 .707 0z"/>
                </svg>
                </>
            }

            
        </button>
        
    )
}

const ForgotPasswordLink = ({ startPasswordResetProcess, cb }) => {
    return(
        <a href="#" onClick={(e) => {
                startPasswordResetProcess( e )
                cb();
            }}
        >
            Forgot Password?
        </a>
    )
}

const InlineInputFieldError = ({ target }) => {
    return(
        <p className="text-danger text-sm m-0" data-field-name={target}>

        </p>
    )
}

const SignInButton = ( {isLoading, canLogin, refSubmit, rounded} ) => {

    const SignInLoader = () => {

        const refLoadingText = React.useRef();
        const refLoadingDots = React.useRef();

        useEffect(() => {
            if( typeof anime === 'function' ){
                var tl = anime.timeline({
                    opacity: [0,1],
                    easing: 'easeOutCubic',
                    duration: 500,
                    loop: true,
                    delay: 1000
                });
                tl
                
                
                .add({
                    targets: refLoadingText.current,
                    translateY: [0, -10],
                    opacity: [1, 0],
                })
                .add({
                    targets: refLoadingDots.current,
                    translateY: [10, 0],
                    opacity: [0, 1],
                    duration: 200,
                    delay: 1200
                }, '-=1500')
                .add({
                    targets: refLoadingDots.current,
                    translateY: [0, -10],
                    opacity: [1, 0],
                })
                .add({
                    targets: refLoadingText.current,
                    translateY: [10, 0],
                    opacity: [0, 1],
                    duration: 200,
                    delay: 1200
                }, '-=1500')
            }
        }, []);

        return(
            <>
                <span ref={refLoadingText}>
                    Signing in
                </span>
                <div
                    ref={refLoadingDots}
                    className='position-absolute mt-n2 la-ball-pulse la-dark la-sm'
                >
                    <div></div>
                    <div></div>
                    <div></div>
                </div>
            </>
        )
    };

    return(
        <button type="submit"
            ref={refSubmit}
            disabled={isLoading || !canLogin}
            className={`
                btn position-relative border border-aw-webinar bg-aw-webinar text-white  ${rounded ? 'rounded-lg' : 'rounded-0'}
            `}
        >
            {
                isLoading ?
                <div className="d-flex flex-column position-relative justify-content-center align-items-center">
                    
                    <SignInLoader />
                </div>
                :
                'Sign In'
            }
        </button>
    )
}


const ErrorMessage = ( {credErrMessage, setCredErrMessage} ) => {
    return(
        <div className="alert alert-warning rounded-0 py-1 px-3 mb-0 text-sm d-flex justify-content-between align-items-center" >
            <span>
                {credErrMessage}
            </span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-x-lg" viewBox="0 0 16 16"
                onClick={e => setCredErrMessage( null )}
            >
                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
            </svg>

        </div>
    )
}

const Title = ( {text} ) => {
    const ref = useRef();
    return(
    <h4 ref={ref} className=" text-2xl font-semibold mb-3 ">
        {text}
    </h4>
)}

const ResetButton = ({ handlePasswordReset, isLoading, disabled = false, cb = () => {} }) => {

    const refButton = React.useRef();
    useMinheight( refButton );
    return(
        <button
            disabled={isLoading || disabled}
            className="position-relative border border-aw-webinar bg-aw-webinar text-white rounded-0 d-flex justify-content-center align-items-center"
            onClick={() => {
                handlePasswordReset();
                cb();
            }}
            ref={refButton}
        >
            { isLoading ? 
                <LoadingDots />
                : "Reset"
            }
        </button>
    )
}

const LoadingDots = ( {className} ) => (
    <div
        className={`position-absolute mt-n2 la-ball-pulse la-dark la-sm ${className}`}
    >
        <div></div>
        <div></div>
        <div></div>
    </div>
)

const InlineMessage = ({ message }) => {
    const ref = React.useRef();
    useEffect( () => {
        if( typeof anime === 'function' && ref?.current ){
            anime({
                targets: ref.current,
                opacity: [0, 1],
                translateX: [10, 0],
            })
        }
    }, [ref] )
    return(
        <p ref={ref} className="text-sm m-0 d-inline-block mb-0" dangerouslySetInnerHTML={{__html: message}}></p>
    )

}